"use client";

import React from "react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Loader, Tag, Layers } from "lucide-react";
import { WP_Query } from "@/integrations/wordpress/wp_query";


const getCurrentQuery = () => {
  if (typeof window !== "undefined" && window.wvcClient) {
    return window.wvcClient.getCurrentWPQueryParams();
  }
  return {};
};

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div className="aspect-square w-full animate-pulse rounded-xl bg-muted/30" />
          <div className="space-y-6 py-4">
            <div className="h-10 w-3/4 animate-pulse rounded bg-muted/30" />
            <div className="h-6 w-1/4 animate-pulse rounded bg-muted/30" />
            <div className="space-y-2">
              <div className="h-4 w-full animate-pulse rounded bg-muted/30" />
              <div className="h-4 w-full animate-pulse rounded bg-muted/30" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-muted/30" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto flex min-h-[400px] flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-bold text-destructive">Error Loading Product</h2>
        <p className="mt-2 text-muted-foreground">Unable to load product details. Please try again later.</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="container mx-auto flex min-h-[400px] flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-bold">Product Not Found</h2>
        <p className="mt-2 text-muted-foreground">The product you are looking for does not exist or has been removed.</p>
        <Link to="/" className="mt-6 text-primary hover:underline">
          Return to Home
        </Link>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount = product.discounted_price !== undefined && product.discounted_price < product.price;

  return (
    <div className="container mx-auto px-4 py-12 md:py-16">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
        {}
        <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          <AspectRatio ratio={1}>
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                loading="eager"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                No Image Available
              </div>
            )}
          </AspectRatio>
          {hasDiscount && (
            <Badge className="absolute top-4 left-4 bg-destructive text-white hover:bg-destructive/90">
              Sale
            </Badge>
          )}
        </div>

        {}
        <div className="flex flex-col justify-center">
          <div className="mb-6">
            <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
              {product.title}
            </h1>
            
            <div className="mt-4 flex items-baseline gap-3">
              {hasDiscount ? (
                <>
                  <span className="text-3xl font-bold text-primary">
                    ${product.discounted_price?.toFixed(2)}
                  </span>
                  <span className="text-xl text-muted-foreground line-through decoration-destructive/50">
                    ${product.price.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-3xl font-bold text-primary">
                  ${product.price.toFixed(2)}
                </span>
              )}
            </div>
          </div>

          {}
          {product.short_description && (
            <div 
              className="prose prose-neutral dark:prose-invert mb-8 max-w-none text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          {}
          <div className="mb-8 rounded-xl border border-border bg-card/50 p-6 backdrop-blur-sm">
            <AddToCartButton
              productId={product.id}
              variant="default"
              size="lg"
              className="w-full"
              buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-lg h-12"
              text="Add to Cart"
              showQuantity={true}
            />
          </div>

          <Separator className="my-6" />

          {}
          <div className="space-y-4 text-sm">
            {product.categories && product.categories.length > 0 && (
              <div className="flex items-start gap-3">
                <Layers className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div className="flex flex-wrap gap-2">
                  <span className="font-medium text-foreground">Categories:</span>
                  {product.categories.map((cat, i) => (
                    <Link 
                      key={cat.id} 
                      to={cat.link} 
                      data-index={i}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      {cat.name}{i < product.categories.length - 1 ? "," : ""}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex items-start gap-3">
                <Tag className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div className="flex flex-wrap gap-2">
                  <span className="font-medium text-foreground">Tags:</span>
                  {product.tags.map((tag, i) => (
                    <Link 
                      key={tag.id} 
                      to={tag.link}
                      data-index={i}
                      className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
                    >
                      {tag.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Separator className="my-6" />

          {}
          {product.long_description && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Product Details</h3>
              <div 
                className="prose prose-neutral dark:prose-invert max-w-none text-muted-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                dangerouslySetInnerHTML={{ __html: product.long_description }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SingleProductTemplateSection() {
  const currentWPQueryParams = getCurrentQuery();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-product-template-section" className="bg-background text-foreground">
      <EcommerceProductsProvider wp_query={wp_query}>
        <SingleProductConsumer />
      </EcommerceProductsProvider>
    </section>
  );
}